import { SidebarFieldName, SidebarFieldOrderItem } from '@/api';
import { organizationData } from '@/store/helpers';
import { computed, Ref } from 'vue';

// Re-export the API types for convenience
export { SidebarFieldName };
export type { SidebarFieldOrderItem };

// Prefix for promoted custom fields (e.g., "cf:abc-123")
// Note: Must match backend constant in sidebar-field-order.types.ts
export const CUSTOM_FIELD_PREFIX = 'cf:';

// Default order value for fields not found in the order list
const DEFAULT_FIELD_ORDER = 999;

// Helper to check if a field name is a promoted custom field
export function isPromotedCustomField(fieldName: string): boolean {
  return fieldName.startsWith(CUSTOM_FIELD_PREFIX);
}

// Helper to extract custom field ID from a promoted custom field name
export function getCustomFieldId(fieldName: string): string | null {
  if (!isPromotedCustomField(fieldName)) return null;
  return fieldName.slice(CUSTOM_FIELD_PREFIX.length);
}

// Helper to create a promoted custom field name from an ID
export function createPromotedCustomFieldName(customFieldId: string): string {
  return `${CUSTOM_FIELD_PREFIX}${customFieldId}`;
}

// Check if a field name is a core sidebar field (not a custom field)
export function isCoreField(fieldName: string): boolean {
  return Object.values(SidebarFieldName).includes(
    fieldName as SidebarFieldName
  );
}

const _defaultSidebarFieldOrder: {
  [K in SidebarFieldName]: SidebarFieldOrderItem;
} = {
  [SidebarFieldName.TEAM]: {
    fieldName: SidebarFieldName.TEAM,
    hidden: false,
  },
  [SidebarFieldName.ASSIGNED_TO]: {
    fieldName: SidebarFieldName.ASSIGNED_TO,
    hidden: false,
  },
  [SidebarFieldName.CYCLE_BOARD]: {
    fieldName: SidebarFieldName.CYCLE_BOARD,
    hidden: false,
  },
  [SidebarFieldName.STATUS]: {
    fieldName: SidebarFieldName.STATUS,
    hidden: false,
  },
  [SidebarFieldName.PRIORITY]: {
    fieldName: SidebarFieldName.PRIORITY,
    hidden: false,
  },
  [SidebarFieldName.POINT_ESTIMATE]: {
    fieldName: SidebarFieldName.POINT_ESTIMATE,
    hidden: false,
  },
  [SidebarFieldName.TIME_ESTIMATE]: {
    fieldName: SidebarFieldName.TIME_ESTIMATE,
    hidden: false,
  },
  [SidebarFieldName.TIME_SPENT]: {
    fieldName: SidebarFieldName.TIME_SPENT,
    hidden: false,
  },
  [SidebarFieldName.START_DATE]: {
    fieldName: SidebarFieldName.START_DATE,
    hidden: false,
  },
  [SidebarFieldName.DUE_DATE]: {
    fieldName: SidebarFieldName.DUE_DATE,
    hidden: false,
  },
  [SidebarFieldName.PROGRESS_TRACKER]: {
    fieldName: SidebarFieldName.PROGRESS_TRACKER,
    hidden: false,
  },
  [SidebarFieldName.CUSTOM_FIELDS]: {
    fieldName: SidebarFieldName.CUSTOM_FIELDS,
    hidden: false,
  },
};

export const defaultSidebarFieldOrder: SidebarFieldOrderItem[] = Object.values(
  _defaultSidebarFieldOrder
);

/**
 * Check if sidebar field order matches the default configuration.
 * Returns true if:
 * 1. No promoted custom fields exist (no cf: prefix fields)
 * 2. All core fields are visible (hidden: false)
 * 3. Fields are in the correct default order
 */
export function isDefaultSidebarOrder(items: SidebarFieldOrderItem[]): boolean {
  // If there are ANY promoted custom fields, it's not default order
  const hasPromotedCustomFields = items.some((item) =>
    isPromotedCustomField(item.fieldName)
  );
  if (hasPromotedCustomFields) return false;

  // Must have exactly the same number of fields as default
  if (items.length !== defaultSidebarFieldOrder.length) return false;

  // Check each field matches default order and visibility
  return items.every((item, index) => {
    const defaultItem = defaultSidebarFieldOrder[index];
    return (
      item.fieldName === defaultItem.fieldName &&
      item.hidden === defaultItem.hidden
    );
  });
}

export function useSidebarFieldOrder({
  workTypeId,
}: { workTypeId?: Ref<string | undefined> } = {}) {
  const { workTypes } = organizationData();

  // Get the raw sidebar field order items (including promoted custom fields)
  const sidebarFieldOrderItems = computed<SidebarFieldOrderItem[]>(() => {
    const id = workTypeId?.value;
    if (id && workTypes.value?.length) {
      const workType = workTypes.value.find((o) => o.id === id);
      if (workType && workType.sidebarFieldOrders?.length > 0) {
        return workType.sidebarFieldOrders;
      }
    }
    return defaultSidebarFieldOrder;
  });

  // Get visible field names (for backward compatibility)
  const sidebarFieldOrder = computed<string[]>(() => {
    return sidebarFieldOrderItems.value
      .filter((field) => !field.hidden)
      .map((field) => field.fieldName);
  });

  const sidebarFieldOrderWithHidden = computed<SidebarFieldOrderItem[]>(() => {
    return sidebarFieldOrderItems.value;
  });

  // Get the custom fields group item
  const customFieldsGroupItem = computed<SidebarFieldOrderItem | undefined>(
    () => {
      return sidebarFieldOrderItems.value.find(
        (item) => item.fieldName === SidebarFieldName.CUSTOM_FIELDS
      );
    }
  );

  // Get promoted custom field IDs (those that appear as separate entries)
  const promotedCustomFieldIds = computed<string[]>(() => {
    return sidebarFieldOrderItems.value
      .filter((item) => isPromotedCustomField(item.fieldName))
      .map((item) => getCustomFieldId(item.fieldName)!)
      .filter(Boolean);
  });

  // Check if a specific custom field is promoted
  const isCustomFieldPromoted = (customFieldId: string): boolean => {
    return promotedCustomFieldIds.value.includes(customFieldId);
  };

  // Get the order index for a field (including promoted custom fields)
  const getFieldOrder = (fieldName: string): number => {
    const index = sidebarFieldOrder.value.indexOf(fieldName);
    return index === -1 ? DEFAULT_FIELD_ORDER : index;
  };

  // Check if a field is visible
  const isFieldVisible = (fieldName: string): boolean => {
    const fieldConfig = sidebarFieldOrderItems.value.find(
      (f) => f.fieldName === fieldName
    );
    return fieldConfig ? !fieldConfig.hidden : true;
  };

  return {
    sidebarFieldOrder,
    sidebarFieldOrderWithHidden,
    sidebarFieldOrderItems,
    customFieldsGroupItem,
    promotedCustomFieldIds,
    isCustomFieldPromoted,
    getFieldOrder,
    isFieldVisible,
  };
}
